/**
 * 来訪の型・問い合わせ(`/api/reception/*`。設計書 §9.1・§9.2、計画 第 4 段)。
 * 権限の真実は API 側。画面は詳細の `can` でボタンを出し分ける。
 * 来訪者の氏名・会社・メールは、拠点の viewer には null で来る(`pii_hidden`)。
 */
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback } from "react";

import { useApi, useMe } from "../../lib/useApi";
import { hasFeature } from "../../lib/features";

export const CORE = "/api/reception";

export type VisitStatus = "scheduled" | "in_progress" | "completed" | "no_show" | "cancelled_by_host";

export type VisitorBrief = {
  id: number;
  visit_id: number;
  status: string;
  send_invite: boolean;
  invite_status: string;
  has_email: boolean;
  assurance: string | null;
  checkin_route: string | null;
  checked_in_at: string | null;
  verified_at: string | null;
  verified_method: string | null;
  pii_hidden: boolean;
  name: string | null;
  name_kana: string | null;
  company: string | null;
  email: string | null;
};

export type VisitorDetail = VisitorBrief & {
  /** 個人情報を消した時刻(保有期間・個別の匿名化。第 7 段)。消していなければ null */
  anonymized_at?: string | null;
  current_generation: number;
  ever_invite_maybe_delivered: boolean;
  delivery_available: boolean;
  delivery_expires_at: string | null;
  consent_declared: boolean;
  last_mail: { id: number; kind: string; status: string; created_at: string | null; finished_at: string | null } | null;
  active_call: { id: number; status: string; started_at: string | null; acked_at: string | null } | null;
};

type VisitBase = {
  id: number;
  kind: string;
  location_id: number;
  location: { id: number; name: string; timezone: string } | null;
  host_employee_id: number;
  host: { id: number; name: string | null; status: string | null } | null;
  starts_at: string;
  ends_at: string;
  visit_date: string;
  expected_headcount: number | null;
  status: VisitStatus;
  cancelled_at: string | null;
  completed_at: string | null;
  created_at: string;
  title: string | null;
  note: string | null;
};

export type VisitListItem = VisitBase & {
  visitor_count: number;
  checked_in_count: number;
  visitors: VisitorBrief[];
  can_edit: boolean;
};

export type VisitDetail = VisitBase & {
  cancel_reason?: string | null;
  /** 要対応(取消の知らせが来訪者へ届かなかった など)。外すと null */
  needs_attention_at?: string | null;
  attention_reason?: string | null;
  visitors: VisitorDetail[];
  can: {
    view_pii: boolean;
    edit: boolean;
    reschedule: boolean;
    cancel: boolean;
    add_visitor: boolean;
    ack: boolean;
    verify_operator: boolean;
    verify_host: boolean;
    resolve_attention?: boolean;
  };
  scopes: string[];
};

/** 要対応の理由(`visit.attention_reason`)の表示 */
export const ATTENTION_REASON: Record<string, string> = {
  cancel_mail_undelivered: "取消の知らせが来訪者へ届いていない。来訪者へ直接連絡してください",
};

export type MyLocation = { id: number; name: string; timezone: string; enabled: boolean; role: string };

export type VisitorInput = {
  name: string;
  name_kana?: string;
  company?: string;
  email?: string | null;
  send_invite: boolean;
  consent_declared?: boolean;
};

export const VISIT_STATUS: Record<string, { label: string; tone: "success" | "warning" | "danger" | "info" | "neutral" }> = {
  scheduled: { label: "予定", tone: "info" },
  in_progress: { label: "来訪中", tone: "warning" },
  completed: { label: "終了", tone: "success" },
  no_show: { label: "来訪なし", tone: "neutral" },
  cancelled_by_host: { label: "取消", tone: "danger" },
};

export const VISITOR_STATUS: Record<string, string> = {
  registered: "登録済み",
  invited: "招待済み",
  checked_in: "受付済み",
  checked_out: "退館",
  no_show: "来訪なし",
  removed: "外した",
};

export { INVITE_STATUS, INVITE_STATUS_HELP, NOTIFICATION_STATUS } from "./mailStatus";

export const MAIL_KIND: Record<string, string> = {
  invite: "招待",
  invite_reschedule: "日時変更",
  invite_changed: "変更",
  visit_cancel: "取消",
};

export function useVisitsEnabled() {
  const me = useMe();
  return hasFeature(me.data, "reception.core");
}

/** 二重押し対策のキー(画面を開くたび・送るたびに作る)。 */
export function newIdemKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** `/api/reception` に JSON を送る。`idem` があれば `Idempotency-Key` を付ける。 */
export function useCoreSend() {
  const api = useApi();
  return useCallback(
    <T>(method: string, path: string, body?: unknown, idem?: string) =>
      api.request<T>(CORE + path, {
        method,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        ...(idem ? { headers: { "Idempotency-Key": idem } } : {}),
      }),
    [api],
  );
}

export function useMyLocations() {
  const api = useApi();
  const enabled = useVisitsEnabled();
  return useQuery({
    queryKey: ["reception", "my-locations"],
    queryFn: () => api.request<{ items: MyLocation[]; unassigned: boolean }>(CORE + "/locations"),
    enabled,
  });
}

export function useVisits(params: URLSearchParams) {
  const api = useApi();
  const enabled = useVisitsEnabled();
  const qs = params.toString();
  return useQuery({
    queryKey: ["reception", "visits", qs],
    queryFn: () => api.request<{ items: VisitListItem[]; total: number }>(`${CORE}/visits?${qs}`),
    enabled,
  });
}

export function useVisit(id: number | null) {
  const api = useApi();
  const enabled = useVisitsEnabled();
  return useQuery({
    queryKey: ["reception", "visit", id],
    queryFn: () => api.request<VisitDetail>(`${CORE}/visits/${id}`),
    enabled: enabled && id !== null,
  });
}

/** 「2026-10-08 10:00〜11:00」(日をまたげば終わりにも日付)。 */
export function fmtRange(s: string, e: string): string {
  const a = dayjs(s);
  const b = dayjs(e);
  return `${a.format("YYYY-MM-DD HH:mm")}〜${b.isSame(a, "day") ? b.format("HH:mm") : b.format("MM-DD HH:mm")}`;
}

/** 来訪履歴の 1 行(1 行 = 来訪者 1 人。第 7 段)。viewer には PII が null(`pii_hidden`)、匿名化済みは空 */
export type HistoryRow = {
  visit_id: number;
  visitor_id: number;
  visit_date: string;
  starts_at: string;
  ends_at: string;
  location_id: number;
  location_name: string;
  kind: string;
  visit_status: string;
  host_employee_id: number;
  host_name: string | null;
  visitor_status: string;
  assurance: string | null;
  checkin_route: string | null;
  checked_in_at: string | null;
  verified_method: string | null;
  anonymized: boolean;
  pii_hidden: boolean;
  name: string | null;
  name_kana: string | null;
  company: string | null;
  email: string | null;
  title: string | null;
};

export const ASSURANCE: Record<string, string> = {
  claimed: "名乗り",
  verified: "本人確認済み",
};

export const CHECKIN_ROUTE: Record<string, string> = {
  qr: "QR",
  code: "6 桁コード",
  lookup: "氏名検索",
  walkin: "飛び込み",
};

export function useHistory(params: URLSearchParams) {
  const api = useApi();
  const enabled = useVisitsEnabled();
  const qs = params.toString();
  return useQuery({
    queryKey: ["reception", "history", qs],
    queryFn: () =>
      api.request<{ items: HistoryRow[]; total: number; pii_columns: boolean }>(`${CORE}/history?${qs}`),
    enabled,
  });
}
